import { useState } from 'react';
import {
    Box,
    Text,
    Flex,
    Button,
    Icon,
    Heading,
    useToast,
    useDisclosure,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/react';
import DescriptionHtml from 'components/molecules/DescriptionHtml';
import MDropZone from 'components/molecules/MDropZone';
import PageHeader from 'layout/pageHeader';
import icons from 'shared/icons';
import { linkStyle } from 'shared/styles';
import { useTranslation } from 'helpers/languages/translations';
import { useNavigate } from 'react-router';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { TClusterError, TClusterSuccess, useUploadClusterMutation } from 'core/API/clusterApi';
import { useHandleExcelExport } from './hooks/useHandleExport';
import useRetailerMultiSelect from 'hooks/useRetailerMultiSelect';

const ClusterUpload = ({ page }: { page: 'cluster' | 'shop-cluster' | 'product-cluster' }) => {
    const translate = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();
    const handleExport = useHandleExcelExport();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [uploadCluster, { isLoading }] = useUploadClusterMutation();
    const { RetailerMultiSelect, retailerIds } = useRetailerMultiSelect();

    const goBack = () => navigate(-1);

    const handleChangeFile = (file: File | null) => {
        if (file?.type === EXCEL_FILE_TYPE) {
            setUploadedFile(file);
        } else {
            if (file !== null)
                toast({
                    title: translate('error.upload.file-invalid-type.xlsx'),
                    status: 'error',
                    isClosable: true,
                });
            setUploadedFile(null);
        }
    };

    const uploadFromExcel = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('type', page);

            const resp = await uploadCluster(bodyFormData);
            const { message: errorMessage, missingHeaders } = (resp as TClusterError).error?.data || {};
            const title = errorMessage || (resp as { data: TClusterSuccess }).data.message;

            const hasError = !!errorMessage;

            if (!hasError) {
                setUploadedFile(null);
                goBack();
            }

            toast({
                title: translate(title, { missingHeaders: (missingHeaders as string[])?.join(', ') }),
                status: hasError ? 'error' : 'success',
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderModal = () => {
        return (
            <Modal isCentered isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader tabIndex={-1}>{translate('product.upload.retailer.selectModal.title')}</ModalHeader>
                    <ModalCloseButton mt="2" isDisabled={isLoading} />
                    <ModalBody fontSize="sm">
                        {translate('secto.upload.selectModal.description')}
                        <RetailerMultiSelect />
                    </ModalBody>
                    <ModalFooter>
                        <Button isDisabled={isLoading} variant="outline" size="sm" mr={3} onClick={onClose}>
                            {translate('product.upload.modal.cancel')}
                        </Button>
                        <Button
                            w="40%"
                            isDisabled={!retailerIds.length}
                            onClick={async () => {
                                await handleExport(page, retailerIds);
                                onClose();
                            }}
                            my={2}
                            size="sm"
                            colorScheme="blue"
                        >
                            {translate('secto.upload.templateButton')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const handleDownloadTemplate = () => {
        if (page === 'shop-cluster') {
            onOpen();
        } else {
            handleExport(page);
        }
    };

    const renderDescriptionSection = () => {
        const UploadIcon = icons.upload;

        return (
            <Flex w="40%" justifyContent="flex-start" direction="column" textAlign="start" mr={5}>
                <Text mb={5}>
                    {translate('product.upload.recommendation', {
                        template: (
                            <Text onClick={handleDownloadTemplate} as="span" {...linkStyle}>
                                {translate('product.upload.downloadTemplate')}
                            </Text>
                        ),
                    })}
                </Text>

                <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={uploadFromExcel}
                        isLoading={isLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate(`${page}.upload.apply`)}
                    </Button>
                </Flex>
                {renderModal()}
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader
                pageTitle={translate(`${page}.upload.header`) as string}
                icon={{ name: 'arrowBack', props: { onClick: goBack } }}
            />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate(`${page}.upload.uploadFile`)}
                    </Heading>
                </Flex>
                <Flex direction="row">
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey={`${page}.upload.description`} />
                </Flex>
            </Box>
        </Box>
    );
};

export default ClusterUpload;
